import { Injectable } from '@angular/core';
import { take } from 'rxjs/operators';

import { Constants } from '../../../providers/constants/constants';
import { OrganizationService } from '../organization/organization.service';
import { SessionService } from '../session/session.service';
import { ToolsService } from '../tools/tools.service';

/**
 * Estructura mínima del usuario en sesión requerida para validar el estado del plan.
 */
interface UserSession {
  nombres: string;
  apellidos: string;
  organizaciones: {
    _id: string;
    plan_vigencia?: {
      pendientepago?: boolean;
    };
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

/**
 * Servicio centralizado para validar el estado de pago del plan de la organización.
 * Consulta la información actualizada de la organización y notifica al usuario
 * cuando el plan tiene un pago pendiente.
 */
@Injectable({
  providedIn: 'root'
})
export class PlanStatusService {

  constructor(
    private organizationService: OrganizationService,
    private toolsService: ToolsService,
    private sessionService: SessionService,
  ) {}

  /**
   * Valida si la organización del usuario en sesión tiene el plan al día.
   * Consulta el API de organización, actualiza los datos en `localStorage` y,
   * si el plan está pendiente de pago, muestra una modal informativa con el
   * contacto de soporte.
   *
   * La validación no bloquea la navegación; solo informa al usuario del estado
   * del plan. Se omite silenciosamente si no hay sesión activa o datos inválidos.
   *
   * @returns void
   */
  validarEstadoPlan(): void {
    const userRaw = localStorage.getItem(Constants.localStorage.user);
    if (!userRaw) {
      return;
    }

    let userSesion: UserSession;
    try {
      userSesion = JSON.parse(userRaw);
    } catch {
      // Sesión corrupta o con formato inválido; no se puede validar el plan
      return;
    }

    const organizationId = userSesion?.organizaciones?._id;
    if (!organizationId) {
      return;
    }

    this.organizationService.getOrganization(organizationId)
      .pipe(take(1))
      .subscribe({
        next: (response: { type: string; data?: UserSession['organizaciones']; message?: { messageDescription: string } }) => {
          if (response.type === Constants.services.types.success && response.data) {
            // Sincroniza la organización en sesión con la respuesta del API
            userSesion.organizaciones = response.data;
            this.sessionService.updateUser(userSesion);

            // Notifica al usuario si el plan tiene un pago pendiente
            if (response.data.plan_vigencia?.pendientepago) {
              this.toolsService.alertNotificacionSimple({
                titulo: Constants.mensajesInformativos.pendientePago.titulo,
                mensaje: `Hola <strong>${userSesion.nombres} ${userSesion.apellidos},</strong> ${Constants.mensajesInformativos.pendientePago.mensaje} <a href="https://wa.me/57${Constants.contactAgendate.supportTeam.whatsapp}">${Constants.contactAgendate.supportTeam.whatsapp}</a>`
              });
            }
          } else if (response.type === Constants.services.types.error) {
            this.toolsService.presentToast(response.message?.messageDescription ?? 'Error al validar el plan');
          }
        },
        error: (err) => {
          this.toolsService.statusService(err);
        }
      });
  }
}
